// src/templateManager/components/tabs/RAGConfigTab.jsx
import React, { useState, useEffect } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { authFetch } from '../../../utils/authFetch';
import {
  Card,
  Stack,
  Switch,
  Checkbox,
  Text,
  Badge,
  Group,
  Divider,
  Alert,
  Loader,
  MultiSelect
} from '@mantine/core';
import { IconDatabase, IconInfoCircle, IconShieldCheck } from '@tabler/icons-react';

/**
 * RAGConfigTab - Configure RAG-based generation for autopilot
 * Controls whether to use policy embeddings for action generation
 */
function RAGConfigTab() {
  const { control, watch } = useFormContext();

  const useControlRag = watch('ragConfig.useControlRag') || false;

  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch available policies
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        setLoading(true);

        // Fetch policies
        const policyResp = await authFetch('/.netlify/functions/listAvailablePolicies', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({})
        });
        const policyData = await policyResp.json();
        setPolicies(policyData.policies || []);

      } catch (error) {
        console.error('[RAGConfigTab] Error fetching options:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOptions();
  }, []);

  return (
    <Stack gap="lg">
      {/* RAG Overview */}
      <Alert icon={<IconInfoCircle size={16} />} color="blue">
        <Text size="sm">
          <strong>RAG (Retrieval-Augmented Generation)</strong> uses your uploaded policies and frameworks
          to generate evidence-based control measures. When enabled, the system will search relevant policy
          documents and use them to create specific, compliant controls for each risk.
        </Text>
      </Alert>

      {/* Control Generation Configuration */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Group justify="space-between">
            <Text size="lg" fw={700}>Control/Action Generation</Text>
            <Badge color={useControlRag ? 'green' : 'gray'}>
              {useControlRag ? 'RAG Enabled' : 'Standard AI'}
            </Badge>
          </Group>

          <Text size="sm" c="dimmed">
            Configure how control measures and mitigating actions are generated in autopilot mode
          </Text>

          <Divider />

          {/* Enable Control RAG */}
          <Controller
            name="ragConfig.useControlRag"
            control={control}
            defaultValue={false}
            render={({ field }) => (
              <Switch
                checked={field.value || false}
                onChange={(event) => field.onChange(event.currentTarget.checked)}
                label={
                  <div>
                    <Text size="sm" fw={500}>Use Policy-Based Control Generation</Text>
                    <Text size="xs" c="dimmed">
                      Generate controls using uploaded policy documents instead of standard AI generation
                    </Text>
                  </div>
                }
                size="md"
              />
            )}
          />

          {useControlRag && (
            <>
              <Divider />

              {/* Policy Types Selection */}
              <div>
                <Text size="sm" fw={500} mb="xs">
                  Policy Types <Text component="span" c="red">*</Text>
                </Text>
                <Text size="xs" c="dimmed" mb="md">
                  Select which types of policies to use for control generation
                </Text>

                <Controller
                  name="ragConfig.policyTypes"
                  control={control}
                  defaultValue={['governance', 'security', 'ai-control']}
                  render={({ field }) => (
                    <Stack gap="xs">
                      <Checkbox
                        checked={field.value?.includes('governance') || false}
                        onChange={(event) => {
                          const checked = event.currentTarget.checked;
                          const current = field.value || [];
                          field.onChange(
                            checked
                              ? [...current, 'governance']
                              : current.filter(v => v !== 'governance')
                          );
                        }}
                        label={
                          <Group gap="xs">
                            <IconShieldCheck size={16} />
                            <div>
                              <Text size="sm">Governance Policies</Text>
                              <Text size="xs" c="dimmed">Corporate governance, compliance frameworks</Text>
                            </div>
                          </Group>
                        }
                      />
                      <Checkbox
                        checked={field.value?.includes('security') || false}
                        onChange={(event) => {
                          const checked = event.currentTarget.checked;
                          const current = field.value || [];
                          field.onChange(
                            checked
                              ? [...current, 'security']
                              : current.filter(v => v !== 'security')
                          );
                        }}
                        label={
                          <Group gap="xs">
                            <IconShieldCheck size={16} />
                            <div>
                              <Text size="sm">Security Policies</Text>
                              <Text size="xs" c="dimmed">Information security, data protection</Text>
                            </div>
                          </Group>
                        }
                      />
                      <Checkbox
                        checked={field.value?.includes('ai-control') || false}
                        onChange={(event) => {
                          const checked = event.currentTarget.checked;
                          const current = field.value || [];
                          field.onChange(
                            checked
                              ? [...current, 'ai-control']
                              : current.filter(v => v !== 'ai-control')
                          );
                        }}
                        label={
                          <Group gap="xs">
                            <IconShieldCheck size={16} />
                            <div>
                              <Text size="sm">AI Control Policies</Text>
                              <Text size="xs" c="dimmed">AI-specific controls, model governance</Text>
                            </div>
                          </Group>
                        }
                      />
                    </Stack>
                  )}
                />
              </div>

              <Divider />

              {/* Optional: Specific Policy Documents */}
              <div>
                <Text size="sm" fw={500} mb="xs">
                  Specific Policy Documents <Badge size="xs">Optional</Badge>
                </Text>
                <Text size="xs" c="dimmed" mb="md">
                  Narrow down to specific policy files (leave empty to use all policies of selected types)
                </Text>

                {loading ? (
                  <Group gap="xs">
                    <Loader size="xs" />
                    <Text size="sm" c="dimmed">Loading available policies...</Text>
                  </Group>
                ) : (
                  <Controller
                    name="ragConfig.policyDocuments"
                    control={control}
                    defaultValue={[]}
                    render={({ field }) => (
                      <MultiSelect
                        {...field}
                        data={policies}
                        placeholder="Select specific policies (optional)"
                        searchable
                        clearable
                        description={`${policies.length} policy documents available`}
                      />
                    )}
                  />
                )}
              </div>
            </>
          )}

          {!useControlRag && (
            <Alert icon={<IconDatabase size={16} />} color="gray">
              <Text size="sm">
                Standard AI generation is enabled. Controls will be generated using general AI knowledge
                without referencing specific policy documents.
              </Text>
            </Alert>
          )}
        </Stack>
      </Card>

      {/* Future: Scenario Generation (placeholder) */}
      <Card withBorder p="lg" opacity={0.6}>
        <Stack gap="md">
          <Group justify="space-between">
            <Text size="lg" fw={700}>Scenario/Risk Generation</Text>
            <Badge color="gray">Coming Soon</Badge>
          </Group>

          <Text size="sm" c="dimmed">
            Future enhancement: Use risk catalogues for scenario generation
          </Text>

          <Controller
            name="ragConfig.useScenarioRag"
            control={control}
            defaultValue={false}
            render={({ field }) => (
              <Switch
                checked={field.value || false}
                onChange={(event) => field.onChange(event.currentTarget.checked)}
                disabled
                label={
                  <div>
                    <Text size="sm" fw={500} c="dimmed">Use Catalogue-Based Scenario Generation</Text>
                    <Text size="xs" c="dimmed">
                      Generate scenarios using uploaded risk catalogues (NIST, ISO, etc.)
                    </Text>
                  </div>
                }
                size="md"
              />
            )}
          />
        </Stack>
      </Card>

      {/* Help Section */}
      <Card withBorder p="lg" bg="blue.0">
        <Stack gap="sm">
          <Group gap="xs">
            <IconInfoCircle size={20} />
            <Text size="sm" fw={600}>How RAG Works</Text>
          </Group>

          <Text size="xs">
            1. <strong>User completes autopilot narrative:</strong> The system captures the AI model context
          </Text>
          <Text size="xs">
            2. <strong>Risk identification:</strong> System identifies risks for the model
          </Text>
          <Text size="xs">
            3. <strong>Policy retrieval:</strong> Searches your policy embeddings for relevant requirements
          </Text>
          <Text size="xs">
            4. <strong>Control generation:</strong> Creates evidence-based controls citing specific policies
          </Text>
          <Text size="xs">
            5. <strong>Automatic fallback:</strong> If no relevant policies are found, uses standard AI generation
          </Text>
        </Stack>
      </Card>
    </Stack>
  );
}

export default RAGConfigTab;
